/**
 * Notification API — RTK Query hooks for notifications and preferences
 *
 * Endpoints:
 * - GET /api/v1/notifications/ — List notifications
 * - POST /api/v1/notifications/mark_read/ — Mark as read
 * - GET /api/v1/notifications/unread_count/ — Get unread count
 * - GET /api/v1/notification-preferences/me/ — Get user preferences
 * - PATCH /api/v1/notification-preferences/me/ — Update user preferences
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

const unreadCountBaseQuery = async (args, api, extraOptions) => {
  const result = await baseQueryWithReauth(args, api, extraOptions);

  if (result.error?.status === 404) {
    return {
      data: {
        unread_count: 0,
        total_count: 0,
      },
    };
  }

  return result;
};

export const notificationApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Notification', 'NotificationCount', 'NotificationPreference', 'NotificationRule'],
  endpoints: (builder) => ({
    // =========================================================================
    // NOTIFICATIONS
    // =========================================================================

    /**
     * Fetch paginated list of notifications for current user
     * GET /api/v1/notifications/?is_read=false&ordering=-created_at
     */
    getNotifications: builder.query({
      query: ({ page = 1, limit = 20, isRead = null, category = null } = {}) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('page_size', limit);
        if (isRead !== null) params.append('is_read', isRead);
        if (category) params.append('category', category);
        return `/api/v1/notifications/?${params.toString()}`;
      },
      providesTags: ['Notification'],
      keepUnusedDataFor: 60, // Cache for 60 seconds
    }),

    /**
     * Get total unread notification count
     * GET /api/v1/notifications/unread_count/
     */
    getUnreadCount: builder.query({
      async queryFn(_arg, api, extraOptions) {
        return unreadCountBaseQuery('/api/v1/notifications/unread_count/', api, extraOptions);
      },
      providesTags: ['NotificationCount'],
      pollingInterval: 30000, // Poll every 30 seconds
    }),

    /**
     * Mark single notification as read
     * PATCH /api/v1/notifications/{id}/
     */
    markNotificationRead: builder.mutation({
      query: (notificationId) => ({
        url: `/api/v1/notifications/${notificationId}/`,
        method: 'PATCH',
        body: { is_read: true },
      }),
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),

    /**
     * Mark multiple notifications as read
     * POST /api/v1/notifications/mark_read/
     */
    markMultipleRead: builder.mutation({
      query: (notificationIds) => ({
        url: '/api/v1/notifications/mark_read/',
        method: 'POST',
        body: { notification_ids: notificationIds },
      }),
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),

    /**
     * Mark all notifications as read
     * POST /api/v1/notifications/mark_read/
     */
    markAllRead: builder.mutation({
      query: () => ({
        url: '/api/v1/notifications/mark_read/',
        method: 'POST',
        body: { notification_ids: [] }, // Empty array = mark all
      }),
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),

    /**
     * Mark notification as dismissed
     * PATCH /api/v1/notifications/{id}/mark_dismissed/
     */
    dismissNotification: builder.mutation({
      query: (notificationId) => ({
        url: `/api/v1/notifications/${notificationId}/mark_dismissed/`,
        method: 'PATCH',
      }),
      // SHR-13: dismissing a notification changes the unread count whenever
      // the dismissed notification was unread, but this only invalidated
      // 'Notification' — the bell badge (getUnreadCount, tagged
      // 'NotificationCount') never refetched, so it kept showing a stale
      // count until something else happened to invalidate it.
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),

    // =========================================================================
    // NOTIFICATION PREFERENCES
    // =========================================================================

    /**
     * Get current user's notification preferences
     * GET /api/v1/notification-preferences/me/
     */
    getNotificationPreferences: builder.query({
      query: () => '/api/v1/notification-preferences/me/',
      providesTags: ['NotificationPreference'],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),

    /**
     * Update current user's notification preferences
     * PATCH /api/v1/notification-preferences/me/
     */
    updateNotificationPreferences: builder.mutation({
      query: (preferences) => ({
        url: '/api/v1/notification-preferences/me/',
        method: 'PATCH',
        body: preferences,
      }),
      invalidatesTags: ['NotificationPreference'],
    }),

    // =========================================================================
    // NOTIFICATION RULES (admin-configured alert routing — System Config)
    // =========================================================================

    /**
     * List tenant's notification rules
     * GET /api/v1/notification-rules/
     */
    getNotificationRules: builder.query({
      query: () => '/api/v1/notification-rules/',
      transformResponse: (response) =>
        Array.isArray(response) ? response : (response?.results ?? []),
      providesTags: ['NotificationRule'],
    }),

    /**
     * Create a notification rule (staff only)
     * POST /api/v1/notification-rules/
     */
    createNotificationRule: builder.mutation({
      query: (rule) => ({
        url: '/api/v1/notification-rules/',
        method: 'POST',
        body: rule,
      }),
      invalidatesTags: ['NotificationRule'],
    }),

    /**
     * Update a notification rule (staff only)
     * PATCH /api/v1/notification-rules/{id}/
     */
    updateNotificationRule: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/notification-rules/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['NotificationRule'],
    }),

    /**
     * Delete a notification rule (staff only)
     * DELETE /api/v1/notification-rules/{id}/
     */
    deleteNotificationRule: builder.mutation({
      query: (id) => ({
        url: `/api/v1/notification-rules/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NotificationRule'],
    }),

    /**
     * Actually send a test notification through the rule's configured channels
     * POST /api/v1/notification-rules/{id}/test_send/
     */
    testSendNotificationRule: builder.mutation({
      query: (id) => ({
        url: `/api/v1/notification-rules/${id}/test_send/`,
        method: 'POST',
      }),
      invalidatesTags: ['NotificationRule'],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkMultipleReadMutation,
  useMarkAllReadMutation,
  useDismissNotificationMutation,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useGetNotificationRulesQuery,
  useCreateNotificationRuleMutation,
  useUpdateNotificationRuleMutation,
  useDeleteNotificationRuleMutation,
  useTestSendNotificationRuleMutation,
} = notificationApi;
